import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Brain, Loader2, Wand2, BookOpen, Target, Lightbulb, Image as ImageIcon, X } from 'lucide-react';
import { missionAPI } from '../utils/api';
import { toast } from 'sonner';

const BLOOM_LEVELS = [
  { value: 'Conhecimento', label: 'Conhecimento', description: 'Memorização e recall de fatos', color: 'bg-blue-100 text-blue-700' },
  { value: 'Compreensão', label: 'Compreensão', description: 'Entendimento e interpretação', color: 'bg-green-100 text-green-700' },
  { value: 'Aplicação', label: 'Aplicação', description: 'Aplicar em situações novas', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'Análise', label: 'Análise', description: 'Identificar padrões e relações', color: 'bg-orange-100 text-orange-700' },
  { value: 'Síntese', label: 'Síntese', description: 'Combinar ideias e criar', color: 'bg-purple-100 text-purple-700' },
  { value: 'Avaliação', label: 'Avaliação', description: 'Julgamento e tomada de decisão', color: 'bg-red-100 text-red-700' }
];

const AIMissionGeneratorModal = ({ isOpen, onClose, onMissionGenerated }) => {
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    num_questions: 5,
    difficulty_level: 'Compreensão',
    bloom_levels: [],
    thematic_context: 'Universo Pokémon',
    // 'multiple_choice' = só MC (default), 'essay' = só dissertativa, 'mixed' = misto
    quiz_format: 'multiple_choice'
  });
  const [generating, setGenerating] = useState(false);
  const [generatedMission, setGeneratedMission] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleBloomLevel = (value) => {
    setFormData(prev => {
      const current = prev.bloom_levels || [];
      const newLevels = current.includes(value) ? current.filter(l => l !== value) : [...current, value];
      return { ...prev, bloom_levels: newLevels, difficulty_level: newLevels[0] || prev.difficulty_level };
    });
  };

  const handleGenerate = async () => {
    if (!formData.title.trim() || !formData.subject.trim()) {
      toast.error('Preencha o título e a matéria');
      return;
    }
    const payload = { ...formData };
    if (payload.bloom_levels.length === 0) payload.bloom_levels = [payload.difficulty_level];

    // Translate UI quiz_format -> backend flags
    payload.quiz_format = formData.quiz_format;  // 'multiple_choice' | 'essay' | 'mixed'
    payload.include_essay = formData.quiz_format !== 'multiple_choice';  // backwards-compat

    setGenerating(true);
    setGeneratedMission(null);

    try {
      const response = await missionAPI.generateWithAI(payload);
      setGeneratedMission(response.data);
      toast.success('Missão gerada com sucesso! Revise antes de publicar.');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar missão com IA');
    } finally {
      setGenerating(false);
    }
  };

  const handleEditQuestion = (qIndex, field, value) => {
    setGeneratedMission(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === qIndex ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleEditAlternative = (qIndex, altIndex, value) => {
    setGeneratedMission(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === qIndex 
          ? { ...q, alternatives: q.alternatives.map((a, j) => j === altIndex ? value : a) }
          : q
      )
    }));
  };

  const handleUseMission = () => {
    if (generatedMission) {
      onMissionGenerated(generatedMission);
      handleClose();
    }
  };

  const handleClose = () => {
    setGeneratedMission(null);
    setFormData({
      title: '',
      subject: '',
      num_questions: 5,
      difficulty_level: 'Compreensão',
      bloom_levels: [],
      thematic_context: 'Universo Pokémon',
      quiz_format: 'multiple_choice'
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            Gerar Missão com IA
          </DialogTitle>
          <DialogDescription>
            Defina os parâmetros e deixe a IA criar questões educacionais contextualizadas
          </DialogDescription>
        </DialogHeader>

        {!generatedMission ? (
          // Form to configure AI generation
          <div className="space-y-6 mt-4">
            {/* Title and Subject */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Título da Missão
                </Label>
                <Input
                  id="title"
                  placeholder="Ex: Operações com Frações"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Matéria/Disciplina
                </Label>
                <Input
                  id="subject"
                  placeholder="Ex: Matemática"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                />
              </div>
            </div>

            {/* Number of Questions */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Número de Questões
              </Label>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <button
                    key={num}
                    onClick={() => handleInputChange('num_questions', num)}
                    className={`
                      w-10 h-10 rounded-xl font-bold text-base transition-all
                      ${formData.num_questions === num 
                        ? 'bg-purple-600 text-white shadow-lg scale-105' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}
                    `}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty Level - Bloom's Taxonomy (Multi-select) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                Níveis da Taxonomia de Bloom (selecione um ou mais)
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {BLOOM_LEVELS.map(level => {
                  const isSelected = formData.bloom_levels.includes(level.value);
                  return (
                    <button
                      key={level.value}
                      onClick={() => toggleBloomLevel(level.value)}
                      data-testid={`bloom-${level.value}`}
                      className={`
                        p-3 rounded-xl text-left transition-all border-2
                        ${isSelected
                          ? 'border-purple-500 bg-purple-50 shadow-md ring-1 ring-purple-300'
                          : 'border-transparent bg-slate-50 hover:bg-slate-100'}
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${level.color}`}>
                          {level.label}
                        </span>
                        {isSelected && <span className="text-purple-600 text-xs font-bold">✓</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{level.description}</p>
                    </button>
                  );
                })}
              </div>
              {formData.bloom_levels.length === 0 && (
                <p className="text-xs text-slate-400">Selecione pelo menos um nível. Múltiplos níveis geram questões variadas.</p>
              )}
            </div>

            {/* Quiz Format Selector */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Tipo de Quiz <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'multiple_choice', label: 'Múltipla Escolha', emoji: '✅', desc: '4 alternativas (A-D)' },
                  { value: 'essay', label: 'Dissertativa', emoji: '📝', desc: 'Resposta aberta + IA' },
                  { value: 'mixed', label: 'Misto', emoji: '🎯', desc: 'Combinação dos dois' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`quiz-format-${opt.value}`}
                    onClick={() => handleInputChange('quiz_format', opt.value)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      formData.quiz_format === opt.value
                        ? 'border-purple-500 bg-purple-50 shadow-sm ring-2 ring-purple-200'
                        : 'border-slate-200 bg-white hover:border-purple-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{opt.emoji}</div>
                    <div className="font-bold text-sm text-slate-900">{opt.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
              {formData.quiz_format === 'essay' && (
                <p className="text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                  💡 Quiz dissertativo: a IA gera questões abertas com conceitos-chave. Após cada envio, a IA pré-analisa a resposta do aluno (Verde/Amarelo/Vermelho) e você fecha a correção.
                </p>
              )}
            </div>

            {/* Thematic Context */}
            <div className="space-y-2">
              <Label htmlFor="context" className="flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                Contextualização Temática
              </Label>
              <Textarea
                id="context"
                placeholder="Ex: Contextualizar com o universo Pokémon, usando referências a batalhas e tipos de Pokémon..."
                value={formData.thematic_context}
                onChange={(e) => handleInputChange('thematic_context', e.target.value)}
                rows={3}
              />
              <p className="text-xs text-slate-500">
                Dica: Quanto mais detalhado o contexto, mais criativas serão as questões!
              </p>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full py-6 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Gerando com IA...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Gerar Missão com IA
                </>
              )}
            </Button>
          </div>
        ) : (
          // Preview and edit generated mission
          <div className="space-y-6 mt-4">
            {/* Mission Header */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Gerado por IA</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Título</Label>
                  <Input
                    value={generatedMission.title}
                    onChange={(e) => setGeneratedMission(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 font-semibold"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Descrição</Label>
                  <Textarea
                    value={generatedMission.description}
                    onChange={(e) => setGeneratedMission(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Questões ({generatedMission.questions.length})
              </h3>
              
              {generatedMission.questions.map((question, qIndex) => (
                <div 
                  key={qIndex} 
                  className="p-4 bg-white border border-slate-200 rounded-xl space-y-3"
                >
                  {/* Question number, Bloom tag and XP */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-700">
                        Questão {qIndex + 1}
                      </span>
                      {question.bloom_level && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          BLOOM_LEVELS.find(l => l.value === question.bloom_level)?.color || 'bg-slate-100 text-slate-600'
                        }`} data-testid={`bloom-tag-q${qIndex}`}>
                          {question.bloom_level}
                        </span>
                      )}
                      {question.question_type === 'essay' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700">Dissertativa</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">AURA:</span>
                      <Input
                        type="number"
                        min={5}
                        max={50}
                        value={question.xp_value}
                        onChange={(e) => handleEditQuestion(qIndex, 'xp_value', parseInt(e.target.value) || 10)}
                        className="w-16 h-8 text-center text-sm"
                      />
                    </div>
                  </div>

                  {/* Question text */}
                  <Textarea
                    value={question.question_text}
                    onChange={(e) => handleEditQuestion(qIndex, 'question_text', e.target.value)}
                    rows={2}
                    className="font-medium"
                  />

                  {/* Image upload for this question */}
                  <div className="flex items-center gap-2">
                    {question.image_url ? (
                      <div className="relative group">
                        <img src={question.image_url} alt={question.image_alt || ''} className="h-16 rounded-lg border object-cover" />
                        <button
                          onClick={() => { handleEditQuestion(qIndex, 'image_url', ''); handleEditQuestion(qIndex, 'image_alt', ''); }}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-dashed border-slate-300 text-[11px] text-slate-500 hover:border-blue-400 hover:text-blue-500 cursor-pointer transition-colors">
                        <ImageIcon className="w-3 h-3" /> Imagem
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missions/upload-image`, { method: 'POST', credentials: 'include', body: formData });
                            if (resp.ok) { const d = await resp.json(); handleEditQuestion(qIndex, 'image_url', d.image_url); toast.success('Imagem anexada!'); }
                            else toast.error('Erro no upload');
                          } catch { toast.error('Erro ao enviar'); }
                          e.target.value = '';
                        }} />
                      </label>
                    )}
                    {question.image_url && (
                      <Input value={question.image_alt || ''} onChange={(e) => handleEditQuestion(qIndex, 'image_alt', e.target.value)} placeholder="Texto alternativo..." className="flex-1 h-7 text-xs" />
                    )}
                  </div>

                  {/* Alternatives - only for MC */}
                  {(question.question_type || 'multiple_choice') !== 'essay' && (
                    <div className="grid grid-cols-2 gap-2">
                      {['A', 'B', 'C', 'D'].map((letter, altIndex) => (
                        <div 
                          key={letter}
                          className={`
                            flex items-center gap-2 p-2 rounded-lg border
                            ${question.correct_answer === letter 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-slate-200 bg-slate-50'}
                          `}
                        >
                          <button
                            onClick={() => handleEditQuestion(qIndex, 'correct_answer', letter)}
                            className={`
                              w-8 h-8 rounded-full font-bold text-sm flex-shrink-0
                              ${question.correct_answer === letter 
                                ? 'bg-green-500 text-white' 
                                : 'bg-white border-2 border-slate-300 text-slate-600 hover:border-green-400'}
                            `}
                            title="Clique para definir como resposta correta"
                          >
                            {letter}
                          </button>
                          <Input
                            value={question.alternatives[altIndex] || ''}
                            onChange={(e) => handleEditAlternative(qIndex, altIndex, e.target.value)}
                            className="flex-1 h-8 text-sm"
                            placeholder={`Alternativa ${letter}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Key Concepts - for essay */}
                  {question.question_type === 'essay' && question.key_concepts?.length > 0 && (
                    <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                      <p className="text-xs font-bold text-violet-700 mb-1.5">Conceitos-Chave (Gabarito):</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {question.key_concepts.map((c, ci) => (
                          <span key={ci} className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[11px] rounded-full font-bold">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hint (Dica de Mestre) */}
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Label className="text-xs font-medium text-amber-700 flex items-center gap-1 mb-1">
                      <Lightbulb className="w-3 h-3" />
                      Dica de Mestre
                    </Label>
                    <Textarea
                      value={question.hint}
                      onChange={(e) => handleEditQuestion(qIndex, 'hint', e.target.value)}
                      rows={2}
                      className="text-sm bg-white"
                      placeholder="Uma pista pedagógica que ajuda o aluno a pensar na direção certa..."
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setGeneratedMission(null)}
                className="flex-1"
              >
                Gerar Novamente
              </Button>
              <Button
                onClick={handleUseMission}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              >
                Usar Esta Missão
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIMissionGeneratorModal;
